import { useCallback, useMemo } from 'react';
import { useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import { useDispatch, useSelector } from 'react-redux';
import MapView from '../map/core/MapView';
import MapSelectedDevice from '../map/main/MapSelectedDevice';
import MapPulse from '../map/main/MapPulse.jsx';
import MapAccuracy from '../map/main/MapAccuracy';
import MapGeofence from '../map/MapGeofence';
import MapCurrentLocation from '../map/MapCurrentLocation';
import MapPoi from '../map/MapPoi';
import MapPadding from '../map/MapPadding';
import { devicesActions } from '../store';
import MapDefaultCamera from '../map/main/MapDefaultCamera';
import MapLiveRoutes from '../map/main/MapLiveRoutes';
import MapPositions from '../map/MapPositions';
import MapOverlay from '../map/overlay/MapOverlay';
import MapGeocoder from '../map/geocoder/MapGeocoder';
import MapScale from '../map/MapScale';
import MapNotification from '../map/notification/MapNotification';
import MapControlsPanel from '../map/controls/MapControlsPanel';
import PoiFilterPanel from '../map/controls/PoiFilterPanel';
import GeofenceFilterPanel from '../map/controls/GeofenceFilterPanel';
import { useFilterPanels } from '../map/controls/useMapControls';
import useFeatures from '../common/util/useFeatures';
import {
  WIDGET_POSITIONS,
  WIDGET_DIMENSIONS,
  GAP,
} from '../common/theme/layout';

const MainMap = ({ filteredPositions, selectedPosition, onEventsClick, onMarkerClick: onMarkerClickProp }) => {
  const theme = useTheme();
  const dispatch = useDispatch();

  const desktop = useMediaQuery(theme.breakpoints.up('md'));

  const eventsAvailable = useSelector((state) => !!state.events.items.length);

  const features = useFeatures();

  // Estado dos painéis de filtro
  const { poi: poiFilterOpen, geofence: geofenceFilterOpen, closeFilterPanel } = useFilterPanels();

  // Calcular posições dos painéis usando constantes centralizadas de layout.js
  // POI fica à direita (mais perto dos controles), Geofence à esquerda
  const panelPositions = useMemo(() => {
    const isMobileView = window.innerWidth < 600;

    if (isMobileView) {
      // Mobile: posição fixa à direita (abaixo da TopBar)
      return {
        geofence: { top: WIDGET_POSITIONS.geofenceFilter.top, right: GAP.MEDIUM },
        poi: { top: WIDGET_POSITIONS.poiFilter.top, right: GAP.MEDIUM },
      };
    }

    // Desktop: usa posições do layout.js (já calculadas corretamente)
    return {
      poi: {
        top: WIDGET_POSITIONS.poiFilter.top,
        right: WIDGET_POSITIONS.poiFilter.right,
      },
      geofence: {
        top: WIDGET_POSITIONS.geofenceFilter.top,
        right: WIDGET_POSITIONS.geofenceFilter.right,
      },
    };
  }, []);

  const onMarkerClick = useCallback((_, deviceId) => {
    dispatch(devicesActions.selectId(deviceId));
    // Notifica o componente pai que o clique veio do mapa
    if (onMarkerClickProp) {
      onMarkerClickProp(deviceId);
    }
  }, [dispatch, onMarkerClickProp]);

  return (
    <>
      <MapView>
        <MapOverlay />
        <MapGeofence />
        <MapAccuracy positions={filteredPositions} />
        <MapLiveRoutes deviceIds={filteredPositions.map((p) => p.deviceId)} />
        <MapPositions
          positions={filteredPositions}
          onMarkerClick={onMarkerClick}
          selectedPosition={selectedPosition}
          showStatus
        />
        <MapDefaultCamera />
        <MapSelectedDevice />
        <MapPulse />
        <MapPoi />
      </MapView>
      <MapScale />
      <MapCurrentLocation />
      <MapGeocoder />
      {!features.disableEvents && (
        <MapNotification enabled={eventsAvailable} onClick={onEventsClick} />
      )}
      <MapControlsPanel />
      <PoiFilterPanel
        open={poiFilterOpen}
        onClose={() => closeFilterPanel('poi')}
        initialPosition={panelPositions.poi}
      />
      <GeofenceFilterPanel
        open={geofenceFilterOpen}
        onClose={() => closeFilterPanel('geofence')}
        initialPosition={panelPositions.geofence}
      />
      {desktop && (
        <MapPadding start={parseInt(theme.dimensions.drawerWidthDesktop, 10) + parseInt(theme.spacing(1.5), 10)} />
      )}
    </>
  );
};

export default MainMap;
